import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  tableCellClasses,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Select,
  MenuItem
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MdOutlineDateRange } from 'react-icons/md';

// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFoundAgreement from '../../components/SearchNotFoundAgreement';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import AgreementToolbar from './AgreementToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getAgreementsList } from '../../store/Slice/Agreement/AgreementSlice';
import useAgreementSelector from '../../store/Slice/Agreement/AgreementSelector';
import { getBankDropdownData, saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { AgreementApi } from '../../utils/Apis';
import useDebounce from '../../utils/useDebounce';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'bankName', label: 'BANK', sortable: true },
  { id: 'noOfBranch', label: 'NO OF BRANCH', sortable: true },
  { id: 'aggrementStartDate', label: 'START ON', sortable: true },
  { id: 'aggrementEndDate', label: 'END ON', sortable: true },
  { id: 'lastUpdatedOn', label: 'MODIFIED ON', sortable: true },
  { id: 'aggrementIsActive', label: 'STATUS', sortable: true },
  { id: 'action', label: 'ACTION', sortable: false }
];

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9'
  }
}));

export default function Agreement() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [endAgreement, setEndAgreement] = useState(false);
  const [agreementId, setAgreementId] = useState(null);
  const [selectStatusValue, setSelectStatusValue] = useState(' ');
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [filterParameter, setFilterParameter] = useState({
    bankId: null
  });
  const [hasNextPage, setHasNextPage] = useState(true);

  const selectStatus = {
    ACTIVE: {
      type_name: 'Active',
      type_code: 'ACTIVE\t',
      type_id: 134
    },
    INACTIVE: {
      type_name: 'Inactive',
      type_code: 'INACTIVE\t',
      type_id: 136
    },
    INCOMPLETE: {
      type_name: 'Incomplete',
      type_code: 'INCOMPLETE\t',
      type_id: 138
    }
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const { AgreementListData } = useAgreementSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const getAgreementsListApi = useCallback(() => {
    let statusResult = '';

    if (selectStatusValue === 134) {
      statusResult = true;
    } else if (selectStatusValue === 136) {
      statusResult = false;
    } else {
      statusResult = null;
    }

    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        sortOrder: order,
        pageNumber: page + 1
      },
      agreementFilterModel: {
        aggrementIsActive: statusResult,
        bankId: filterParameter?.bankId || null,
        fromDate:
          filterDatesRange[0].startDate !== null ? fDateTime2(filterDatesRange[0].startDate) : null,
        toDate:
          filterDatesRange[0].endDate !== null ? fDateTime2(filterDatesRange[0].endDate) : null
      }
    };

    dispatch(getAgreementsList(credentials));
  }, [
    LoginData,
    debouncedSearch,
    order,
    filterDatesRange,
    selectStatusValue,
    filterParameter,
    page,
    rowsPerPage,
    dispatch
  ]);

  useEffect(() => {
    getAgreementsListApi();
  }, [getAgreementsListApi]);

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  const openEndAgreementDialog = (e, rowId) => {
    setAgreementId(rowId);
    setEndAgreement(true);
  };

  const handleEndAgreement = async () => {
    const credential = {
      agreementId: agreementId,
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    const result = await dispatch(saveApiData(AgreementApi?.agreementTerminate, credential));
    if (result?.status === 'success') {
      getAgreementsListApi();
      setEndAgreement(false);
    }
  };

  const handleRequestSort = (property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  useEffect(() => {
    if (!AgreementListData) {
      setHasNextPage(true);
      return;
    }
    setHasNextPage(AgreementListData.length >= rowsPerPage);
  }, [AgreementListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, selectStatusValue, filterParameter, filterDatesRange]);

  const totalRecords = Array.isArray(AgreementListData) ? AgreementListData.length : 0;

  return (
    <Page title="Partner Management System || Agreement">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumb */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title with Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <ArticleOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Agreement
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                View and manage partner agreements with validity and status.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* Main Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <AgreementToolbar
            filterName={filterName}
            setFilterName={setFilterName}
            parent="agreement"
            selectStatusValue={selectStatusValue}
            setSelectStatusValue={setSelectStatusValue}
            filterDatesRange={filterDatesRange}
            setFilterDatesRange={setFilterDatesRange}
            selectStatus={selectStatus}
            setFilterParameter={setFilterParameter}
            filterParameter={filterParameter}
          />

          {/* Table */}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, minHeight: 380 }}>
              <Table>
                <thead>
                  <TableRow>
                    {TABLE_HEAD.map((headCell) => (
                      <StyledHeadCell
                        key={headCell.id}
                        onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                        sx={{
                          cursor: headCell.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          ...(headCell.id === 'action' ? { textAlign: 'center' } : {})
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          justifyContent={headCell.id === 'action' ? 'center' : 'flex-start'}
                        >
                          <span>{headCell.label}</span>
                          {headCell.sortable && (
                            <SwapVertIcon
                              sx={{
                                fontSize: 16,
                                color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                              }}
                            />
                          )}
                        </Stack>
                      </StyledHeadCell>
                    ))}
                  </TableRow>
                </thead>
                <TableBody>
                  {Array.isArray(AgreementListData) &&
                    AgreementListData.map((item, index) => {
                      const statusActive = item.aggrementIsActive;
                      const statusLabel =
                        statusActive === null
                          ? 'Incomplete'
                          : statusActive === true
                          ? 'Active'
                          : 'Inactive';

                      return (
                        <TableRow
                          hover
                          key={item.agreementId || index}
                          tabIndex={-1}
                          sx={{
                            '&:hover': { backgroundColor: 'rgba(2, 132, 199, 0.03)' }
                          }}
                        >
                          {/* Bank Name */}
                          <StyledTableCell sx={{ fontWeight: 500, color: '#334155' }}>
                            {item.bankName || 'N/A'}
                          </StyledTableCell>

                          {/* No of Branch */}
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {item.noOfBranch !== undefined && item.noOfBranch !== null
                              ? item.noOfBranch
                              : 'N/A'}
                          </StyledTableCell>

                          {/* Start On */}
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {item.aggrementStartDate ? fDate(item.aggrementStartDate) : 'N/A'}
                          </StyledTableCell>

                          {/* End On */}
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {item.aggrementEndDate ? fDate(item.aggrementEndDate) : 'N/A'}
                          </StyledTableCell>

                          {/* Modified On */}
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {item.lastUpdatedOn ? fDate(item.lastUpdatedOn) : 'N/A'}
                          </StyledTableCell>

                          {/* Status Badge */}
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.35,
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                ...(statusActive === true
                                  ? { bgcolor: '#dcfce7', color: '#16a34a' }
                                  : statusActive === false
                                  ? { bgcolor: '#fee2e2', color: '#dc2626' }
                                  : { bgcolor: '#fef3c7', color: '#d97706' })
                              }}
                            >
                              {statusLabel}
                            </Box>
                          </StyledTableCell>

                          {/* Action Buttons */}
                          <StyledTableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              {/* View Action */}
                              <Tooltip title="View Agreement" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    localStorage.setItem('viewAgreementId', item.agreementId);
                                    localStorage.removeItem('reviewData');
                                    localStorage.removeItem('commisssionType');
                                    navigate('/agreement/view', {
                                      state: {
                                        rowId: item.agreementId,
                                        record: item
                                      }
                                    });
                                  }}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'rgba(2, 132, 199, 0.08)',
                                    color: '#0284c7',
                                    borderRadius: '8px',
                                    '&:hover': {
                                      bgcolor: 'rgba(2, 132, 199, 0.16)',
                                      color: '#0369a1'
                                    }
                                  }}
                                >
                                  <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>

                              {/* Edit Action */}
                              {item.aggrementIsActive !== false && (
                                <Tooltip title="Edit Agreement" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      localStorage.removeItem('reviewData');
                                      localStorage.removeItem('commisssionType');
                                      navigate('/agreement/edit', {
                                        state: {
                                          rowId: item.agreementId,
                                          record: item
                                        }
                                      });
                                    }}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: 'rgba(2, 132, 199, 0.08)',
                                      color: '#0284c7',
                                      borderRadius: '8px',
                                      '&:hover': {
                                        bgcolor: 'rgba(2, 132, 199, 0.16)',
                                        color: '#0369a1'
                                      }
                                    }}
                                  >
                                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* End Agreement Action */}
                              <Tooltip title="End Agreement" arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={
                                      item.aggrementIsActive === null ||
                                      item.aggrementIsActive === true
                                        ? false
                                        : true
                                    }
                                    onClick={(e) => {
                                      openEndAgreementDialog(e, item.agreementId);
                                    }}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: 'rgba(239, 68, 68, 0.08)',
                                      color: '#ef4444',
                                      borderRadius: '8px',
                                      '&:hover': {
                                        bgcolor: 'rgba(239, 68, 68, 0.16)',
                                        color: '#dc2626'
                                      },
                                      '&.Mui-disabled': {
                                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                                        color: 'rgba(0, 0, 0, 0.26)'
                                      }
                                    }}
                                  >
                                    <MdOutlineDateRange size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>

                {(!AgreementListData || AgreementListData.length === 0) && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 5 }}>
                        <SearchNotFoundAgreement searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Pagination Footer matching screenshot */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            {/* Left: Range text */}
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
              Showing {totalRecords > 0 ? page * rowsPerPage + 1 : 0} to{' '}
              {totalRecords > 0 ? page * rowsPerPage + totalRecords : 0} records
            </Typography>

            {/* Right: Rows per page + Page Buttons */}
            <Stack direction="row" alignItems="center" spacing={2.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Rows per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    fontSize: '13px',
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>

              {/* Page Navigation Buttons */}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.5,
                    color: '#64748b',
                    '&:disabled': { opacity: 0.4 }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Button
                  size="small"
                  sx={{
                    minWidth: 32,
                    height: 32,
                    p: 0,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 700,
                    bgcolor: '#0284c7 !important',
                    color: '#ffffff !important'
                  }}
                >
                  {page + 1}
                </Button>

                <IconButton
                  size="small"
                  disabled={!hasNextPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.5,
                    color: '#64748b',
                    '&:disabled': { opacity: 0.4 }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog
          fullWidth
          maxWidth="xs"
          open={endAgreement}
          onClose={() => setEndAgreement(false)}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle
            sx={{
              p: 2.5,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(239, 68, 68, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ef4444'
                }}
              >
                <WarningAmberIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                End Agreement
              </Typography>
            </Stack>
            <IconButton
              onClick={() => setEndAgreement(false)}
              size="small"
              sx={{
                color: '#64748b',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Typography sx={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
              Are you sure you want to end this agreement? This action will mark the agreement as
              inactive.
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              bgcolor: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setEndAgreement(false)}
              sx={{
                borderRadius: '8px',
                borderColor: '#cbd5e1',
                color: '#64748b',
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'none',
                px: 2.5,
                py: 0.8,
                '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEndAgreement}
              sx={{
                borderRadius: '8px',
                bgcolor: '#ef4444',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'none',
                px: 2.5,
                py: 0.8,
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.25)',
                '&:hover': { bgcolor: '#dc2626' }
              }}
            >
              Confirm End Agreement
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
